<script setup>
// One security behind an allocation share (docs/analytics/SPEC.md, decisions 10, 14): logo, name | ticker,
// share bar, share of the whole portfolio and value in RUB, in the same columns as the share rows.
import { ref } from 'vue';
import { intlLocale } from '../i18n';
import { isPhone } from '../isPhone';
import ShareBar from './ShareBar.vue';

const props = defineProps({
  // { position, value, percent }
  item: {
    type: Object,
    required: true,
  },
  color: {
    type: String,
    default: null,
  },
  // Bar color for securities inside an opened share (their own dot is not shown).
  barColor: {
    type: String,
    default: null,
  },
  number: {
    type: Number,
    default: null,
  },
  active: {
    type: Boolean,
    default: false,
  },
  indent: {
    type: Boolean,
    default: true,
  },
  bars: {
    type: Boolean,
    default: true,
  },
});

const logoBroken = ref(false);

function title() {
  const p = props.item.position;
  return p.name || p.ticker || p.instrument_uid || '?';
}

function initial() {
  return title().trim().charAt(0).toUpperCase();
}

function money(value) {
  return new Intl.NumberFormat(intlLocale(), { style: 'currency', currency: 'RUB', maximumFractionDigits: 0 }).format(value);
}

function percent(value) {
  return `${new Intl.NumberFormat(intlLocale(), { maximumFractionDigits: 1, minimumFractionDigits: 1 }).format(value)} %`;
}
</script>

<template>
  <!-- Phone: name with the sum under it, the percent on the right (docs/design/SPEC.md, decision 14) -->
  <div
    v-if="isPhone"
    class="flex min-h-12 items-center gap-3 py-2"
    :class="indent ? 'pl-5' : ''"
  >
    <span
      v-if="number !== null"
      class="w-5 shrink-0 text-right text-[0.8125rem] tabular-nums text-slate-500"
    >{{ number }}</span>
    <span
      v-if="color"
      class="size-2.5 shrink-0 rounded-full"
      :style="{ backgroundColor: color }"
      aria-hidden="true"
    />
    <img
      v-if="item.position.logo_url && !logoBroken"
      :src="item.position.logo_url"
      alt=""
      class="size-8 shrink-0 rounded-full bg-slate-800 object-cover"
      loading="lazy"
      @error="logoBroken = true"
    >
    <span
      v-else
      class="flex size-8 shrink-0 items-center justify-center rounded-full bg-slate-700 text-[0.6875rem] font-semibold text-white"
      :style="item.position.logo_color ? { backgroundColor: item.position.logo_color } : null"
      aria-hidden="true"
    >{{ initial() }}</span>
    <span class="min-w-0 flex-1">
      <span class="block truncate text-[0.9375rem] leading-5 text-slate-100">{{ title() }}</span>
      <span class="mt-0.5 block text-[0.8125rem] leading-4 tabular-nums text-slate-500">{{ money(item.value) }}</span>
    </span>
    <span class="shrink-0 text-[0.9375rem] tabular-nums text-slate-100">{{ percent(item.percent) }}</span>
    <span
      v-if="bars"
      class="w-4 shrink-0"
      aria-hidden="true"
    />
  </div>
  <div
    v-else
    class="flex items-center gap-3 rounded px-2 py-1 text-[0.8125rem] lg:text-xs"
    :class="active ? 'bg-slate-800/80' : ''"
    :title="title()"
  >
    <span
      class="flex min-w-0 flex-1 items-center gap-2"
      :class="[indent ? 'pl-5' : '', bars ? 'sm:w-60 sm:flex-none lg:w-72' : '']"
    >
      <span
        v-if="number !== null"
        class="w-5 shrink-0 text-right text-[0.6875rem] tabular-nums text-slate-500 lg:text-[0.625rem]"
      >{{ number }}</span>
      <span
        v-if="color"
        class="size-2 shrink-0 rounded-full"
        :style="{ backgroundColor: color }"
        aria-hidden="true"
      />
      <img
        v-if="item.position.logo_url && !logoBroken"
        :src="item.position.logo_url"
        alt=""
        class="size-5 shrink-0 rounded-full bg-slate-800 object-cover lg:size-4"
        loading="lazy"
        @error="logoBroken = true"
      >
      <span
        v-else
        class="flex size-5 shrink-0 items-center justify-center rounded-full bg-slate-700 text-[0.5625rem] font-semibold text-white lg:size-4 lg:text-[0.5rem]"
        :style="item.position.logo_color ? { backgroundColor: item.position.logo_color } : null"
        aria-hidden="true"
      >{{ initial() }}</span>
      <span class="min-w-0 truncate text-slate-200">
        {{ title() }}
        <span
          v-if="item.position.name && item.position.ticker"
          :class="item.position.ticker.length > 6 ? 'hidden lg:inline' : ''"
        >
          <span class="px-1 text-[0.6875rem] text-slate-600 lg:text-[0.625rem]">|</span>
          <span class="text-[0.6875rem] text-slate-500 lg:text-[0.625rem]">{{ item.position.ticker }}</span>
        </span>
      </span>
    </span>
    <ShareBar
      v-if="bars"
      :percent="item.percent"
      :color="barColor ?? color ?? undefined"
      thin
    />
    <span
      class="flex shrink-0 flex-col items-end sm:w-auto sm:flex-row sm:items-center"
      :class="bars ? 'w-20' : 'w-16'"
    >
      <span class="tabular-nums text-slate-100 sm:w-14 sm:text-right">{{ percent(item.percent) }}</span>
      <span
        class="text-[0.6875rem] tabular-nums text-slate-500 sm:text-right sm:text-xs lg:text-[0.6875rem]"
        :class="bars ? 'sm:w-28' : 'sm:w-24'"
      >{{ money(item.value) }}</span>
    </span>
    <!-- Room for the chevron column of share rows; the companies list has none -->
    <span
      v-if="bars"
      class="w-3.5 shrink-0"
      aria-hidden="true"
    />
  </div>
</template>
